import React from 'react'
import { css } from '@emotion/css'
import { Placement } from '../popper'
import { theme as deprecatedTheme } from '../../styled'
import {
  theme,
  ThemeColors
} from '@contrast-security-inc/design-system-foundations'

const defaultArrowBorderWidth = '5px'
const defaultArrowBorderColor = theme.color.neutral100
const defaultArrowShadowColor =
  deprecatedTheme.secondaryColors.gray040WithMiddleOpacity

type ArrowSize = 'small' | 'large'

const arrowSizeMeasurements: Record<
  ArrowSize,
  {
    readonly lineHeight: number
    readonly lineHeightTopCorrection: number
    readonly lineHeightBottomCorrection: number
  }
> = {
  small: {
    lineHeight: 10,
    lineHeightTopCorrection: 1.5,
    lineHeightBottomCorrection: 2
  },
  large: {
    lineHeight: 20,
    lineHeightTopCorrection: 3,
    lineHeightBottomCorrection: 4
  }
}

function arrowSizeToHeight(size: ArrowSize) {
  const { lineHeight, lineHeightTopCorrection, lineHeightBottomCorrection } =
    arrowSizeMeasurements[size]
  return lineHeight - lineHeightTopCorrection - lineHeightBottomCorrection
}

export const largeArrowHeight = arrowSizeToHeight('large')
export const smallArrowHeight = arrowSizeToHeight('small')
export const largeArrowWithBorderHeight = arrowSizeToHeight('large') + 1
export const smallArrowWithBorderHeight = arrowSizeToHeight('small') + 1

interface Props {
  readonly getRef: React.Ref<any>
  readonly style?: React.CSSProperties
  readonly placement: Placement
  readonly arrowBorderColor?: string
  readonly arrowBorderWidth?: string
  readonly arrowShadowColor?: string
  readonly arrowColor?: ThemeColors
}

export const PopperArrow = ({
  getRef,
  style,
  placement,
  arrowBorderWidth,
  arrowBorderColor,
  arrowShadowColor
}: Props) => {
  const borderColor = arrowBorderColor || defaultArrowBorderColor
  const borderWidth = arrowBorderWidth || defaultArrowBorderWidth
  const shadowColor = arrowShadowColor || defaultArrowShadowColor

  return (
    <div
      ref={getRef}
      style={style}
      data-popper-placement={placement}
      className={css`
        &[data-popper-placement^='top'],
        &[data-popper-placement^='bottom'],
        &[data-popper-placement^='right'],
        &[data-popper-placement^='left'] {
          border-width: ${borderWidth};
          transform: rotate(45deg);
          background: ${borderColor};
        }

        &[data-popper-placement^='top'] {
          border-color: ${borderColor} transparent transparent transparent;
          box-shadow: 1px 1px 1px ${shadowColor};
          bottom: -${borderWidth};
          left: calc(50% - 5px);
          margin-top: 0;
          margin-bottom: 0;
        }

        &[data-popper-placement^='bottom'] {
          border-color: transparent transparent ${borderColor} transparent;
          box-shadow: -1px -1px 1px ${shadowColor};
          top: -${borderWidth};
          left: calc(50% - 5px);
          margin-top: 0;
          margin-bottom: 0;
        }

        &[data-popper-placement^='right'] {
          border-color: transparent ${borderColor} transparent transparent;
          box-shadow: -1px 1px 1px ${shadowColor};
          left: -${borderWidth};
          top: calc(50% - 5px);
          margin-left: 0;
          margin-right: 0;
        }

        &[data-popper-placement^='left'] {
          border-color: transparent transparent transparent ${borderColor};
          box-shadow: 1px -1px 1px ${shadowColor};
          right: -${borderWidth};
          top: calc(50% - 5px);
          margin-left: 0;
          margin-right: 0;
        }
      `}
    />
  )
}
